/**
 * LineChart - Line chart wrapper using react-chartjs-2 with EduQ brand colors
 * 
 * Features:
 * - EduQ brand color scheme
 * - Responsive design
 * - Customizable options
 * - Loading state
 * - Empty state
 */

import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// EduQ brand colors
const BRAND_COLORS = {
  blue: '#0066FF',
  cyan: '#00D9B5',
  blueHover: '#0052CC',
  cyanHover: '#00B89A',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  secondary: '#6B7280'
};

const DEFAULT_COLORS = [
  BRAND_COLORS.blue,
  BRAND_COLORS.cyan,
  BRAND_COLORS.success,
  BRAND_COLORS.warning,
  BRAND_COLORS.info,
  BRAND_COLORS.error,
  BRAND_COLORS.secondary
];

const LineChart = ({
  data,
  title,
  height = 300,
  loading = false,
  emptyMessage = 'No data available',
  options = {},
  colors = DEFAULT_COLORS,
  fill = false,
  tension = 0.4,
  showLegend = true,
  showGrid = true,
  className = ''
}) => {
  if (loading) {
    return (
      <div className={`admin-chart admin-chart--loading ${className}`} style={{ height }}>
        <div className="admin-chart__loading">
          <div className="admin-spinner"></div>
          <p>Loading chart...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className={`admin-chart admin-chart--empty ${className}`} style={{ height }}>
        <div className="admin-chart__empty">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Apply colors to datasets
  const chartData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      borderColor: dataset.borderColor || colors[index % colors.length],
      backgroundColor: dataset.backgroundColor || (fill ? `${colors[index % colors.length]}33` : 'transparent'),
      tension: dataset.tension !== undefined ? dataset.tension : tension,
      fill: dataset.fill !== undefined ? dataset.fill : fill,
      pointBackgroundColor: dataset.pointBackgroundColor || colors[index % colors.length],
      pointBorderColor: dataset.pointBorderColor || '#FFFFFF',
      pointBorderWidth: dataset.pointBorderWidth || 2,
      pointRadius: dataset.pointRadius || 4,
      pointHoverRadius: dataset.pointHoverRadius || 6
    }))
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: '600',
          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: '600'
        },
        bodyFont: {
          size: 12
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: true,
        usePointStyle: true
      }
    },
    scales: {
      x: {
        grid: {
          display: showGrid,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          display: showGrid,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    }
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    },
    scales: {
      ...defaultOptions.scales,
      ...options.scales
    }
  };

  return (
    <div className={`admin-chart ${className}`} style={{ height }}>
      <Line data={chartData} options={mergedOptions} />
    </div>
  );
};

export default LineChart;
